// SPDX-License-Identifier: GPL-3.0-or-later
// The state of the stream overlay and the reducer that applies the Worker frames (OverlayMessage).
// The overlay is read-only. A frame comes from the Durable Object, but this module does not trust it:
// it checks the shape, cuts every string, and runs every name through `sanitizeDisplayName`.
// The claim code never reaches the overlay. This module has no field for it.

import {
  type FeaturedEventWire,
  type FeaturedStatus,
  type FeaturedTurnPublic,
  type HowToPlayCard,
  type OverlayMessage,
  type QueueEntryPublic,
  sanitizeDisplayName,
  type TurnState,
} from "../src/shared/contracts.ts";
import { MODES } from "../src/table/modes.ts";

/** The banner at the start of a featured turn shows this long (THEME.md section 9.5). */
export const BANNER_MS = 3000;
/** The result card shows this long. */
export const RESULT_MS = 5000;
/** A thank-you line shows this long. */
export const THANKS_MS = 5000;
/** Text fields of a frame are cut to this many code points. */
const TEXT_MAX = 64;

/** The state of the overlay. */
export interface OverlayState {
  /** The last `seq` that was applied. */
  readonly seq: number;
  readonly hidden: boolean;
  readonly howto: HowToPlayCard | null;
  readonly queue: readonly QueueEntryPublic[];
  readonly queueLength: number;
  readonly featured: {
    readonly turn: FeaturedTurnPublic;
    /** Text of the last featured event, or an empty string. */
    readonly lastEvent: string;
  } | null;
  readonly nextUp: string;
  readonly banner: { readonly name: string; readonly untilMs: number } | null;
  readonly result: {
    readonly name: string;
    readonly score: number;
    readonly untilMs: number;
  } | null;
  readonly thanks: {
    readonly name: string;
    readonly turns: number;
    readonly untilMs: number;
  } | null;
}

/** The state before the first snapshot. */
export function initialOverlayState(): OverlayState {
  return {
    seq: 0,
    hidden: false,
    howto: null,
    queue: [],
    queueLength: 0,
    featured: null,
    nextUp: "",
    banner: null,
    result: null,
    thanks: null,
  };
}

// ───────────────────────────── parsing ─────────────────────────────

type Rec = Record<string, unknown>;

const isRec = (v: unknown): v is Rec => typeof v === "object" && v !== null && !Array.isArray(v);
const str = (v: unknown, max = TEXT_MAX): string =>
  typeof v === "string" ? Array.from(v).slice(0, max).join("") : "";
const int = (v: unknown, min: number, max: number): number =>
  typeof v === "number" && Number.isFinite(v) ? Math.min(max, Math.max(min, Math.floor(v))) : min;
const nameOf = (v: unknown): string => sanitizeDisplayName(str(v, 200));

/**
 * The published modes by id. A frame comes from the player's own game, so a mode id is trusted
 * only when it is in this list. The overlay shows the published label, never a label of the frame.
 */
const KNOWN_MODES: ReadonlyMap<string, (typeof MODES)[number]> = new Map(
  MODES.map((m) => [m.id, m]),
);

/** The published mode of an id, or undefined for an unknown id. */
function knownMode(id: unknown): (typeof MODES)[number] | undefined {
  return typeof id === "string" ? KNOWN_MODES.get(id) : undefined;
}

const PHASES: ReadonlySet<string> = new Set([
  "attract",
  "ball_ready",
  "playing",
  "ball_end",
  "game_over",
]);
const TURN_STATES: ReadonlySet<string> = new Set([
  "queued",
  "ready",
  "live",
  "done",
  "returned",
  "expired",
]);
const END_REASONS: ReadonlySet<string> = new Set([
  "game_over",
  "time_limit",
  "operator",
  "disconnect",
]);

function parseStatus(v: unknown): FeaturedStatus | null {
  if (!isRec(v) || typeof v.phase !== "string" || !PHASES.has(v.phase)) return null;
  return {
    phase: v.phase as FeaturedStatus["phase"],
    score: int(v.score, 0, Number.MAX_SAFE_INTEGER),
    ball_number: int(v.ball_number, 0, 10),
    balls_per_game: int(v.balls_per_game, 1, 10),
    balls_in_play: int(v.balls_in_play, 0, 10),
    multiplier: int(v.multiplier, 1, 10),
    // An unknown mode id is dropped: it could carry any text onto the stream.
    active_modes: Array.isArray(v.active_modes)
      ? v.active_modes.slice(0, 8).flatMap((m) => {
          const mode = knownMode(m);
          return mode === undefined ? [] : [mode.id];
        })
      : [],
  };
}

function parseEntry(v: unknown): QueueEntryPublic | null {
  if (!isRec(v) || typeof v.state !== "string" || !TURN_STATES.has(v.state)) return null;
  return {
    entry_id: str(v.entry_id, 40),
    display_name: nameOf(v.display_name),
    state: v.state as TurnState,
    position: int(v.position, 0, 1000),
  };
}

function parseEntries(v: unknown): QueueEntryPublic[] {
  if (!Array.isArray(v)) return [];
  const out: QueueEntryPublic[] = [];
  for (const item of v.slice(0, 10)) {
    const entry = parseEntry(item);
    if (entry !== null) out.push(entry);
  }
  return out;
}

function parseTurn(v: unknown): FeaturedTurnPublic | null {
  if (!isRec(v) || typeof v.state !== "string" || !TURN_STATES.has(v.state)) return null;
  return {
    turn_id: str(v.turn_id, 40),
    display_name: nameOf(v.display_name),
    state: v.state as TurnState,
    status: v.status === null || v.status === undefined ? null : parseStatus(v.status),
    started_at: typeof v.started_at === "string" ? str(v.started_at, 40) : null,
    ends_at: typeof v.ends_at === "string" ? str(v.ends_at, 40) : null,
  };
}

function parseEvent(v: unknown): FeaturedEventWire | null {
  if (!isRec(v)) return null;
  switch (v.type) {
    case "mode_start": {
      // The label of the frame is not used. The published label of the id is.
      const mode = knownMode(v.mode_id);
      return mode === undefined
        ? null
        : { type: "mode_start", mode_id: mode.id, label: mode.label };
    }
    case "mode_end": {
      const mode = knownMode(v.mode_id);
      return mode === undefined
        ? null
        : { type: "mode_end", mode_id: mode.id, completed: v.completed === true };
    }
    case "multiball":
      return {
        type: "multiball",
        balls_in_play: int(v.balls_in_play, 0, 10),
        active: v.active === true,
      };
    case "ball_lost":
      return { type: "ball_lost", ball_number: int(v.ball_number, 0, 10) };
    case "ball_saved":
      return { type: "ball_saved", ball_number: int(v.ball_number, 0, 10) };
    default:
      return null;
  }
}

function parseHowTo(v: unknown): HowToPlayCard | null {
  if (!isRec(v)) return null;
  const lines = (list: unknown, max: number): string[] =>
    Array.isArray(list) ? list.slice(0, max).map((l) => str(l, 200)) : [];
  // Only published modes pass, with their published text.
  const modes = Array.isArray(v.modes)
    ? v.modes.slice(0, 12).flatMap((m) => {
        const mode = isRec(m) ? knownMode(m.id) : undefined;
        return mode === undefined
          ? []
          : [{ id: mode.id, label: mode.label, description: mode.description }];
      })
    : [];
  return {
    title: str(v.title, 80),
    controls: lines(v.controls, 8),
    turn_rule: lines(v.turn_rule, 10),
    modes,
  };
}

type FrameParser = (value: Rec, seq: number) => OverlayMessage | null;

function parseSnapshotFrame(value: Rec, seq: number): OverlayMessage | null {
  const snap = value.snapshot;
  if (!isRec(snap)) return null;
  const howto = parseHowTo(snap.howto);
  const featured =
    snap.featured === null || snap.featured === undefined ? null : parseTurn(snap.featured);
  return {
    type: "snapshot",
    seq,
    snapshot: {
      featured,
      queue: parseEntries(snap.queue),
      queue_length: int(snap.queue_length, 0, 1000),
      howto: howto ?? { title: "", controls: [], turn_rule: [], modes: [] },
      hidden: snap.hidden === true,
    },
  };
}

function parseStartFrame(value: Rec, seq: number): OverlayMessage | null {
  const turn = parseTurn(value.turn);
  return turn === null ? null : { type: "featured_start", seq, turn };
}

function parseUpdateFrame(value: Rec, seq: number): OverlayMessage | null {
  const status = parseStatus(value.status);
  return status === null
    ? null
    : { type: "featured_update", seq, turn_id: str(value.turn_id, 40), status };
}

function parseEventFrame(value: Rec, seq: number): OverlayMessage | null {
  const event = parseEvent(value.event);
  return event === null
    ? null
    : { type: "featured_event", seq, turn_id: str(value.turn_id, 40), event };
}

function parseEndFrame(value: Rec, seq: number): OverlayMessage | null {
  if (typeof value.reason !== "string" || !END_REASONS.has(value.reason)) return null;
  return {
    type: "featured_end",
    seq,
    turn_id: str(value.turn_id, 40),
    final_score: int(value.final_score, 0, Number.MAX_SAFE_INTEGER),
    reason: value.reason as "game_over" | "time_limit" | "operator" | "disconnect",
  };
}

function parseNextUpFrame(value: Rec, seq: number): OverlayMessage | null {
  const entry = parseEntry(value.entry);
  return entry === null ? null : { type: "next_up", seq, entry };
}

function parseQueueFrame(value: Rec, seq: number): OverlayMessage {
  return {
    type: "queue",
    seq,
    queue: parseEntries(value.queue),
    queue_length: int(value.queue_length, 0, 1000),
  };
}

function parseGrantFrame(value: Rec, seq: number): OverlayMessage {
  return {
    type: "grant",
    seq,
    display_name: nameOf(value.display_name),
    turns: int(value.turns, 0, 100),
  };
}

/** One parser for each known frame type. */
const FRAME_PARSERS: Readonly<Record<string, FrameParser>> = {
  snapshot: parseSnapshotFrame,
  featured_start: parseStartFrame,
  featured_update: parseUpdateFrame,
  featured_event: parseEventFrame,
  featured_end: parseEndFrame,
  next_up: parseNextUpFrame,
  queue: parseQueueFrame,
  grant: parseGrantFrame,
};

/** Parse a text frame from the Worker. Anything that is not a known frame gives null. */
export function parseOverlayMessage(data: unknown): OverlayMessage | null {
  if (typeof data !== "string") return null;
  let value: unknown;
  try {
    value = JSON.parse(data);
  } catch {
    return null;
  }
  if (!isRec(value) || typeof value.type !== "string") return null;
  if (value.type === "pong") return { type: "pong" };
  if (!Object.hasOwn(FRAME_PARSERS, value.type)) return null;
  const seq = int(value.seq, 0, Number.MAX_SAFE_INTEGER);
  return FRAME_PARSERS[value.type]?.(value, seq) ?? null;
}

// ───────────────────────────── reducer ─────────────────────────────

/** The line for a featured event. It shows no personal data. */
export function eventLine(ev: FeaturedEventWire): string {
  switch (ev.type) {
    case "mode_start":
      return `MODE: ${ev.label}`;
    case "mode_end":
      return ev.completed ? "MODE COMPLETE" : "MODE ENDED";
    case "multiball":
      return ev.active ? "MULTIBALL" : "MULTIBALL OVER";
    case "ball_lost":
      return "BALL LOST";
    case "ball_saved":
      return "BALL SAVED";
  }
}

type SnapshotMessage = Extract<OverlayMessage, { type: "snapshot" }>;
type FrameMessage = Exclude<OverlayMessage, { type: "pong" | "snapshot" }>;

/** A snapshot replaces the whole state. */
function fromSnapshot(msg: SnapshotMessage): OverlayState {
  const snap = msg.snapshot;
  return {
    ...initialOverlayState(),
    seq: msg.seq,
    hidden: snap.hidden,
    howto: snap.howto,
    queue: snap.queue,
    queueLength: snap.queue_length,
    featured: snap.featured !== null ? { turn: snap.featured, lastEvent: "" } : null,
  };
}

/** The featured turn of the state, when the frame is for that turn. */
function turnOf(state: OverlayState, turnId: string): NonNullable<OverlayState["featured"]> | null {
  return state.featured !== null && state.featured.turn.turn_id === turnId ? state.featured : null;
}

/** A frame of the running turn: a new status, or a new event line. Other turns change nothing. */
function reduceTurnFrame(
  state: OverlayState,
  next: OverlayState,
  msg: Extract<FrameMessage, { type: "featured_update" | "featured_event" }>,
): OverlayState {
  const current = turnOf(state, msg.turn_id);
  if (current === null) return next;
  if (msg.type === "featured_update") {
    return { ...next, featured: { ...current, turn: { ...current.turn, status: msg.status } } };
  }
  return { ...next, featured: { ...current, lastEvent: eventLine(msg.event) } };
}

/** The start and the end of a featured turn: the banner and the result card. */
function reduceTurnEdge(
  state: OverlayState,
  next: OverlayState,
  msg: Extract<FrameMessage, { type: "featured_start" | "featured_end" }>,
  nowMs: number,
): OverlayState {
  if (msg.type === "featured_start") {
    return {
      ...next,
      featured: { turn: msg.turn, lastEvent: "" },
      banner: { name: msg.turn.display_name, untilMs: nowMs + BANNER_MS },
      result: null,
      thanks: null,
      nextUp: "",
    };
  }
  const name = state.featured?.turn.display_name ?? "player";
  return {
    ...next,
    featured: null,
    banner: null,
    result: { name, score: msg.final_score, untilMs: nowMs + RESULT_MS },
  };
}

/** The frames of the waiting list and of the thank-you line. */
function reduceListFrame(
  next: OverlayState,
  msg: Extract<FrameMessage, { type: "next_up" | "queue" | "grant" }>,
  nowMs: number,
): OverlayState {
  switch (msg.type) {
    case "next_up":
      return { ...next, nextUp: msg.entry.display_name };
    case "queue":
      return { ...next, queue: msg.queue, queueLength: msg.queue_length };
    case "grant":
      return {
        ...next,
        thanks: { name: msg.display_name, turns: msg.turns, untilMs: nowMs + THANKS_MS },
      };
  }
}

/** Apply one frame. `nowMs` is the page clock; the banner and the cards expire against it. */
export function reduceOverlay(
  state: OverlayState,
  msg: OverlayMessage,
  nowMs: number,
): OverlayState {
  if (msg.type === "pong") return state;
  if (msg.type === "snapshot") return fromSnapshot(msg);
  if (msg.seq <= state.seq) return state;
  const next = { ...state, seq: msg.seq };
  if (msg.type === "featured_start" || msg.type === "featured_end") {
    return reduceTurnEdge(state, next, msg, nowMs);
  }
  if (msg.type === "featured_update" || msg.type === "featured_event") {
    return reduceTurnFrame(state, next, msg);
  }
  return reduceListFrame(next, msg, nowMs);
}
